import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { supabase } from '@/lib/supabase'
import { DealStatusBadge } from '../components/DealStatusBadge'
import type { DealStatus } from '../components/DealStatusBadge'

export const Route = createFileRoute('/deals_/$dealId')({
  component: DealDetailComponent,
})

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Profile {
  id: string
  email: string
  display_name: string | null
}

type ActivityType = 'note' | 'call' | 'email' | 'meeting' | 'outreach'

interface DealDetail {
  id: string
  case_number: string
  status: DealStatus
  assigned_to: string | null
  created_by: string
  notes: string | null
  created_at: string
  updated_at: string
  assignee: Pick<Profile, 'id' | 'email' | 'display_name'> | null
  creator: Pick<Profile, 'id' | 'email' | 'display_name'>
  auction: {
    property_address: string | null
    surplus: number | null
    auction_date: string
    county: string | null
  } | null
}

interface DealActivity {
  id: string
  deal_id: string
  user_id: string
  activity_type: ActivityType
  content: string
  created_at: string
  author: Pick<Profile, 'id' | 'email' | 'display_name'>
}

interface CaseParticipantForDeal {
  role: 'plaintiff' | 'defendant'
  party_type_code: string | null
  lead_att_name: string | null
  participants: {
    id: string
    party_id: string
    name: string
    is_individual: boolean
  }
}

interface LookupProvider {
  id: string
  name: string
  display_name: string
  enabled: boolean
  cooldown_hours: number
}

interface LookupAttempt {
  id: string
  participant_id: string
  provider_id: string
  attempted_at: string
  status: 'pending' | 'success' | 'error' | 'no_results'
  identity_score: number | null
}

interface ParticipantContact {
  id: string
  participant_id: string
  attempt_id: string
  provider_id: string
  contact_type: 'phone' | 'email' | 'address'
  value: string
  confidence: number | null
  is_incorrect: boolean
  marked_by: string | null
  marked_at: string | null
  last_reported_date: string | null
  metadata: Record<string, unknown> | null
  provider: { display_name: string }
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

function useDeal(dealId: string) {
  return useQuery<DealDetail>({
    queryKey: ['deal', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          id, case_number, status, assigned_to, created_by, notes, created_at, updated_at,
          auction:auctions!deals_case_number_fkey(property_address, surplus, auction_date, county),
          assignee:profiles!deals_assigned_to_fkey(id, email, display_name),
          creator:profiles!deals_created_by_fkey(id, email, display_name)
        `)
        .eq('id', dealId)
        .single()
      if (error) throw new Error(error.message)
      return data as DealDetail
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })
}

function useDealActivities(dealId: string) {
  return useQuery<DealActivity[]>({
    queryKey: ['deal-activities', dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_activities')
        .select(`
          id, deal_id, user_id, activity_type, content, created_at,
          author:profiles!deal_activities_user_id_fkey(id, email, display_name)
        `)
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      return (data ?? []) as DealActivity[]
    },
    staleTime: 1 * 60 * 1000,
    retry: 1,
  })
}

function useCaseParticipants(caseNumber: string) {
  return useQuery<CaseParticipantForDeal[]>({
    queryKey: ['case-participants-for-deal', caseNumber],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('court_cases')
        .select(`
          case_participants(role, party_type_code, lead_att_name, participants(id, party_id, name, is_individual))
        `)
        .eq('case_number', caseNumber)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return (data?.case_participants ?? []) as CaseParticipantForDeal[]
    },
    enabled: !!caseNumber,
    staleTime: 5 * 60 * 1000,
  })
}

function useLookupProviders() {
  return useQuery<LookupProvider[]>({
    queryKey: ['lookup-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lookup_providers')
        .select('id, name, display_name, enabled, cooldown_hours')
        .eq('enabled', true)
      if (error) throw new Error(error.message)
      return (data ?? []) as LookupProvider[]
    },
    staleTime: 10 * 60 * 1000,
  })
}

function useLookupAttempts(participantIds: string[]) {
  return useQuery<LookupAttempt[]>({
    queryKey: ['lookup-attempts', participantIds.join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_lookup_attempts')
        .select('id, participant_id, provider_id, attempted_at, status, identity_score')
        .in('participant_id', participantIds)
        .order('attempted_at', { ascending: false })
      if (error) throw new Error(error.message)
      return (data ?? []) as LookupAttempt[]
    },
    enabled: participantIds.length > 0,
    staleTime: 1 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.some(a => a.status === 'pending') ? 5000 : false,
  })
}

function useParticipantContacts(participantIds: string[]) {
  return useQuery<ParticipantContact[]>({
    queryKey: ['participant-contacts', participantIds.join(',')],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_contacts')
        .select(`
          id, participant_id, attempt_id, provider_id, contact_type, value,
          confidence, is_incorrect, marked_by, marked_at, last_reported_date, metadata,
          provider:lookup_providers(display_name)
        `)
        .in('participant_id', participantIds)
        .order('created_at', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []) as ParticipantContact[]
    },
    enabled: participantIds.length > 0,
    staleTime: 2 * 60 * 1000,
  })
}

function useProfiles() {
  return useQuery<Profile[]>({
    queryKey: ['profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name')
        .order('display_name')
      if (error) throw new Error(error.message)
      return (data ?? []) as Profile[]
    },
    staleTime: 10 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const [year, month, day] = iso.split('T')[0].split('-')
  if (!month) return iso
  return `${month}/${day}/${year}`
}

function displayName(profile: Pick<Profile, 'email' | 'display_name'> | null | undefined): string {
  if (!profile) return '—'
  return profile.display_name || profile.email
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
    </div>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  )
}

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  note: 'Note',
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  outreach: 'Outreach',
}

const ACTIVITY_TYPE_COLORS: Record<ActivityType, string> = {
  note:     'bg-gray-100 text-gray-700',
  call:     'bg-blue-100 text-blue-700',
  email:    'bg-purple-100 text-purple-700',
  meeting:  'bg-yellow-100 text-yellow-700',
  outreach: 'bg-orange-100 text-orange-700',
}

function ActivityTypeBadge({ type }: { type: ActivityType }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTIVITY_TYPE_COLORS[type]}`}>
      {ACTIVITY_TYPE_LABELS[type]}
    </span>
  )
}

function isProviderOnCooldown(
  providerId: string,
  attempts: LookupAttempt[],
  cooldownHours: number
): boolean {
  const last = attempts.find(a => a.provider_id === providerId)
  if (!last) return false
  if (last.status === 'pending') return true
  return Date.now() < new Date(last.attempted_at).getTime() + cooldownHours * 3_600_000
}

interface ParticipantLookupButtonProps {
  participantId: string
  participantName: string
  propertyAddress: string | null
  attempts: LookupAttempt[]
  providers: LookupProvider[]
}

function ParticipantLookupButton({
  participantId,
  participantName,
  propertyAddress,
  attempts,
  providers,
}: ParticipantLookupButtonProps) {
  const [loading, setLoading] = useState(false)
  const [lookupError, setLookupError] = useState('')
  const queryClient = useQueryClient()

  const participantAttempts = attempts.filter(a => a.participant_id === participantId)
  const isPending = participantAttempts.some(a => a.status === 'pending')
  const allOnCooldown = providers.length > 0 && providers.every(p =>
    isProviderOnCooldown(p.id, participantAttempts, p.cooldown_hours)
  )
  const isDisabled = loading || isPending || allOnCooldown

  async function handleLookup() {
    setLoading(true)
    setLookupError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
      const res = await fetch(`${supabaseUrl}/functions/v1/participant-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({
          participant_id: participantId,
          participant_name: participantName,
          property_address: propertyAddress ?? '',
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
      }
      await queryClient.invalidateQueries({ queryKey: ['lookup-attempts'] })
      await queryClient.invalidateQueries({ queryKey: ['participant-contacts'] })
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  let label = 'Lookup Contact'
  if (loading || isPending) {
    label = 'Looking up...'
  } else if (allOnCooldown) {
    const last = participantAttempts[0]
    if (last) {
      const hoursAgo = Math.round((Date.now() - new Date(last.attempted_at).getTime()) / 3_600_000)
      label = hoursAgo < 1 ? 'Looked up recently' : `Looked up ${hoursAgo}h ago`
    }
  }

  const lastSuccess = participantAttempts.find(a => a.status === 'success')
  const lastNoResults = participantAttempts.find(a => a.status === 'no_results')

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={handleLookup}
        disabled={isDisabled}
        title={allOnCooldown ? 'All providers have been attempted within their cooldown window' : 'Look up contact info via configured providers'}
        className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {label}
      </button>
      {lastSuccess?.identity_score != null && (
        <p className="text-xs text-gray-400 text-right">
          Match score: {lastSuccess.identity_score}%
        </p>
      )}
      {!lastSuccess && lastNoResults && (
        <p className="text-xs text-amber-500 text-right">No matches found</p>
      )}
      {lookupError && <p className="text-xs text-red-500 max-w-[160px] text-right">{lookupError}</p>}
    </div>
  )
}

function formatEnformionDate(s: unknown): string {
  if (typeof s !== 'string' || !s) return ''
  const [m, d, y] = s.split('/')
  if (!m || !d || !y) return s
  const date = new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function ContactMetadataTooltip({
  type,
  metadata,
}: {
  type: 'phone' | 'email' | 'address'
  metadata: Record<string, unknown>
}) {
  if (type === 'phone') {
    return (
      <div className="space-y-0.5">
        {metadata.type && <div><span className="text-gray-400">Type:</span> {String(metadata.type)}</div>}
        {metadata.isConnected != null && (
          <div>
            <span className="text-gray-400">Connected:</span>{' '}
            <span className={metadata.isConnected ? 'text-green-600' : 'text-red-500'}>
              {metadata.isConnected ? 'Yes' : 'No'}
            </span>
          </div>
        )}
        {metadata.firstReportedDate && <div><span className="text-gray-400">First seen:</span> {formatEnformionDate(metadata.firstReportedDate)}</div>}
        {metadata.lastReportedDate && <div><span className="text-gray-400">Last seen:</span> {formatEnformionDate(metadata.lastReportedDate)}</div>}
      </div>
    )
  }
  if (type === 'email') {
    return (
      <div className="space-y-0.5">
        {metadata.isValidated != null && (
          <div>
            <span className="text-gray-400">Validated:</span>{' '}
            <span className={metadata.isValidated ? 'text-green-600' : 'text-gray-500'}>
              {metadata.isValidated ? 'Yes' : 'No'}
            </span>
          </div>
        )}
        {metadata.isBusiness != null && (
          <div><span className="text-gray-400">Business:</span> {metadata.isBusiness ? 'Yes' : 'No'}</div>
        )}
      </div>
    )
  }
  // address
  return (
    <div className="space-y-0.5">
      {metadata.firstReportedDate && <div><span className="text-gray-400">First seen:</span> {formatEnformionDate(metadata.firstReportedDate)}</div>}
      {metadata.lastReportedDate && <div><span className="text-gray-400">Last seen:</span> {formatEnformionDate(metadata.lastReportedDate)}</div>}
    </div>
  )
}

const CONTACT_TYPE_LABELS: Record<string, string> = {
  phone: 'Phone',
  email: 'Email',
  address: 'Address',
}

function ParticipantContactList({
  participantId,
  contacts,
  userId,
}: {
  participantId: string
  contacts: ParticipantContact[]
  userId: string
}) {
  const queryClient = useQueryClient()
  const [marking, setMarking] = useState<string | null>(null)

  const participantContacts = contacts.filter(c => c.participant_id === participantId)
  if (participantContacts.length === 0) return null

  const byType = {
    phone: participantContacts
      .filter(c => c.contact_type === 'phone')
      .sort((a, b) => {
        if (!a.last_reported_date && !b.last_reported_date) return 0
        if (!a.last_reported_date) return 1
        if (!b.last_reported_date) return -1
        return b.last_reported_date.localeCompare(a.last_reported_date)
      }),
    email: participantContacts.filter(c => c.contact_type === 'email'),
    address: participantContacts.filter(c => c.contact_type === 'address'),
  }

  async function handleMarkIncorrect(contactId: string) {
    setMarking(contactId)
    await supabase
      .from('participant_contacts')
      .update({
        is_incorrect: true,
        marked_by: userId,
        marked_at: new Date().toISOString(),
      })
      .eq('id', contactId)
    await queryClient.invalidateQueries({ queryKey: ['participant-contacts'] })
    setMarking(null)
  }

  return (
    <div className="mt-3 space-y-2">
      {(['phone', 'email', 'address'] as const).map(type => {
        const items = byType[type]
        if (items.length === 0) return null
        return (
          <div key={type}>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              {CONTACT_TYPE_LABELS[type]}
            </p>
            <div className="space-y-1">
              {items.map(contact => (
                <div
                  key={contact.id}
                  className={`flex items-start justify-between gap-2 text-xs rounded px-2 py-1.5 bg-gray-50 ${contact.is_incorrect ? 'opacity-50' : ''}`}
                >
                  <div className="relative group min-w-0 flex-1">
                    <span className={contact.is_incorrect ? 'line-through text-gray-400' : 'text-gray-800'}>
                      {contact.value}
                    </span>
                    {contact.is_incorrect && (
                      <span className="ml-1.5 text-gray-400">· Reported incorrect</span>
                    )}
                    {contact.metadata && (
                      <div className="absolute left-0 top-full mt-1 z-10 hidden group-hover:block bg-white border border-gray-200 rounded shadow-md px-2.5 py-2 text-xs text-gray-600 whitespace-nowrap">
                        <ContactMetadataTooltip type={contact.contact_type} metadata={contact.metadata} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {contact.confidence != null && (
                      <span className="text-gray-400">{Math.round(contact.confidence * 100)}%</span>
                    )}

                    {!contact.is_incorrect && (
                      <button
                        onClick={() => handleMarkIncorrect(contact.id)}
                        disabled={marking === contact.id}
                        className="text-gray-300 hover:text-red-400 disabled:opacity-50 leading-none"
                        title="Mark as incorrect"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: DealStatus[] = ['new', 'in_progress', 'closed_won', 'closed_lost', 'on_hold']
const STATUS_LABELS: Record<DealStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  on_hold: 'On Hold',
}

const ACTIVITY_TYPES: ActivityType[] = ['note', 'call', 'email', 'meeting', 'outreach']

function DealDetailComponent() {
  const { dealId } = Route.useParams()
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: deal, isLoading: dealLoading, error: dealError } = useDeal(dealId)
  const { data: activities, isLoading: activitiesLoading } = useDealActivities(dealId)
  const { data: profiles } = useProfiles()
  const { data: participants } = useCaseParticipants(deal?.case_number ?? '')

  const participantIds = (participants ?? [])
    .filter(p => p.role === 'defendant')
    .map(p => p.participants.id)

  const { data: providers = [] } = useLookupProviders()
  const { data: attempts = [] } = useLookupAttempts(participantIds)
  const { data: contacts = [] } = useParticipantContacts(participantIds)

  // Editable deal fields
  const [editStatus, setEditStatus] = useState<DealStatus | null>(null)
  const [editAssignedTo, setEditAssignedTo] = useState<string | null | undefined>(undefined)
  const [editNotes, setEditNotes] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Add activity form
  const [activityType, setActivityType] = useState<ActivityType>('note')
  const [activityContent, setActivityContent] = useState('')
  const [addingActivity, setAddingActivity] = useState(false)
  const [activityError, setActivityError] = useState('')

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoading, navigate])

  // Sync local edit state when deal loads
  useEffect(() => {
    if (deal) {
      setEditStatus(deal.status)
      setEditAssignedTo(deal.assigned_to)
      setEditNotes(deal.notes)
    }
  }, [deal])

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  const isDirty = deal && (
    editStatus !== deal.status ||
    editAssignedTo !== deal.assigned_to ||
    (editNotes ?? '') !== (deal.notes ?? '')
  )

  async function handleSave() {
    if (!deal) return
    setSaving(true)
    setSaveError('')
    const { error } = await supabase
      .from('deals')
      .update({
        status: editStatus,
        assigned_to: editAssignedTo ?? null,
        notes: editNotes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', deal.id)
    setSaving(false)
    if (error) {
      setSaveError(error.message)
      return
    }
    await queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
    await queryClient.invalidateQueries({ queryKey: ['deals'] })
  }

  async function handleAddActivity(e: React.FormEvent) {
    e.preventDefault()
    if (!activityContent.trim()) return
    setAddingActivity(true)
    setActivityError('')
    const { error } = await supabase
      .from('deal_activities')
      .insert({
        deal_id: dealId,
        user_id: user.id,
        activity_type: activityType,
        content: activityContent.trim(),
      })
    setAddingActivity(false)
    if (error) {
      setActivityError(error.message)
      return
    }
    setActivityContent('')
    setActivityType('note')
    await queryClient.invalidateQueries({ queryKey: ['deal-activities', dealId] })
  }

  const defendants = (participants ?? []).filter(p => p.role === 'defendant')

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate({ to: '/deals' })}
          className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          ← Back to Deals
        </button>
        {deal && (
          <Link
            to="/cases/$caseNumber"
            params={{ caseNumber: deal.case_number }}
            className="px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
          >
            Case {deal.case_number}
          </Link>
        )}
      </div>

      {dealLoading && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">Loading deal...</p>
        </div>
      )}

      {dealError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error loading deal: {(dealError as Error).message}</p>
        </div>
      )}

      {!dealLoading && !dealError && deal && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Deal Info */}
            <SectionCard title="Deal">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <Field label="Property" value={deal.auction?.property_address} />
                <Field label="Surplus" value={formatCurrency(deal.auction?.surplus ?? null)} />
                <Field label="Auction Date" value={formatDate(deal.auction?.auction_date ?? null)} />
                <Field label="County" value={deal.auction?.county} />
                <Field label="Created By" value={displayName(deal.creator)} />
                <Field label="Created" value={formatDate(deal.created_at)} />

                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</dt>
                  <select
                    value={editStatus ?? deal.status}
                    onChange={e => setEditStatus(e.target.value as DealStatus)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 w-full"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Assigned To</dt>
                  <select
                    value={editAssignedTo ?? ''}
                    onChange={e => setEditAssignedTo(e.target.value || null)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700 w-full"
                  >
                    <option value="">Unassigned</option>
                    {(profiles ?? []).map(p => (
                      <option key={p.id} value={p.id}>{p.display_name || p.email}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Notes</dt>
                  <textarea
                    value={editNotes ?? ''}
                    onChange={e => setEditNotes(e.target.value)}
                    rows={3}
                    className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-700 w-full resize-none"
                    placeholder="Add deal notes..."
                  />
                </div>
              </dl>

              {saveError && (
                <p className="mt-2 text-sm text-red-600">{saveError}</p>
              )}

              {isDirty && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="mt-4 px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </SectionCard>

            {/* Participants */}
            <SectionCard title={`Participants${defendants.length ? ` (${defendants.length})` : ''}`}>
              {defendants.length === 0 ? (
                <p className="text-sm text-gray-500">No defendant records found for this case.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {defendants.map(cp => (
                    <div key={cp.participants.party_id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{cp.participants.name}</p>
                          {cp.lead_att_name && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Atty: {cp.lead_att_name}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">
                            {cp.participants.is_individual ? 'Individual' : 'Entity'}
                            {cp.party_type_code && ` · ${cp.party_type_code}`}
                          </p>
                        </div>
                        <ParticipantLookupButton
                          participantId={cp.participants.id}
                          participantName={cp.participants.name}
                          propertyAddress={deal.auction?.property_address ?? null}
                          attempts={attempts}
                          providers={providers}
                        />
                      </div>
                      <ParticipantContactList
                        participantId={cp.participants.id}
                        contacts={contacts}
                        userId={user.id}
                      />
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Activity Timeline */}
          <SectionCard title="Activity">
            <form onSubmit={handleAddActivity} className="mb-6 space-y-3">
              <div className="flex gap-3">
                <select
                  value={activityType}
                  onChange={e => setActivityType(e.target.value as ActivityType)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700"
                >
                  {ACTIVITY_TYPES.map(t => (
                    <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
                <textarea
                  value={activityContent}
                  onChange={e => setActivityContent(e.target.value)}
                  rows={2}
                  className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-700 resize-none"
                  placeholder="Add a note or log an activity..."
                />
              </div>
              {activityError && (
                <p className="text-sm text-red-600">{activityError}</p>
              )}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={addingActivity || !activityContent.trim()}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {addingActivity ? 'Adding...' : 'Add Activity'}
                </button>
              </div>
            </form>

            {activitiesLoading && (
              <p className="text-sm text-gray-500">Loading activities...</p>
            )}

            {!activitiesLoading && (!activities || activities.length === 0) && (
              <p className="text-sm text-gray-400">No activity yet. Log the first one above.</p>
            )}

            {!activitiesLoading && activities && activities.length > 0 && (
              <div className="space-y-4">
                {activities.map(activity => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="flex-none w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                      {displayName(activity.author).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">{displayName(activity.author)}</span>
                        <ActivityTypeBadge type={activity.activity_type} />
                        <span className="text-xs text-gray-400">{formatDate(activity.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{activity.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  )
}
